import type { Middleware } from "openapi-fetch";
import type { TokenProvider } from "../auth-bridge/tokenProvider";

/**
 * Injects `Authorization: Bearer <access-token>` on every outbound request
 * when a token is available. Omits the header for pre-auth requests (login,
 * password-reset, tenant-branding), which the backend treats as anonymous.
 *
 * The token is re-read on every request (synchronously) so the middleware
 * automatically picks up a rotated token from `refreshBridge` without any
 * cache invalidation.
 */
export function authMiddleware(tokenProvider: TokenProvider): Middleware {
  return {
    onRequest({ request }) {
      const token = tokenProvider.getAccessToken();
      if (token) request.headers.set("Authorization", `Bearer ${token}`);
      // X-Refresh-Audience (CC-277): the refresh-family endpoints that flow
      // through this client (session management + password change) need the
      // routing hint so the backend reads the right per-audience refresh cookie
      // on the shared host. Refresh/logout themselves go through @linkhr/auth's
      // raw fetch and set the header there.
      const path = new URL(request.url).pathname;
      if (path.includes("/me/sessions") || path.endsWith("/me/password")) {
        const audHeaders = tokenProvider.getAudienceHeader?.() ?? {};
        for (const [key, value] of Object.entries(audHeaders)) {
          request.headers.set(key, value);
        }
      }
      return request;
    },
  };
}
